import { money } from "@/lib/api";

const WIDTH = 32;
const LINE = "-".repeat(WIDTH);

const CSS = `@page{size:58mm auto;margin:0}html,body{margin:0;padding:0}pre{font-family:'Courier New',Courier,monospace;font-size:12px;line-height:1.65;letter-spacing:0.3px;width:58mm;padding:8px 4px;white-space:pre-wrap;word-break:break-word}`;
const KITCHEN_CSS = `@page{size:58mm auto;margin:0}html,body{margin:0;padding:0}pre{font-family:'Courier New',Courier,monospace;font-size:15px;font-weight:bold;line-height:1.8;width:58mm;padding:8px 4px;white-space:pre-wrap;word-break:break-word}`;

function center(text) {
  const t = String(text || "").trim();
  if (t.length >= WIDTH) return t;
  return " ".repeat(Math.floor((WIDTH - t.length) / 2)) + t;
}

function row(left, right) {
  const l = String(left ?? "");
  const r = String(right ?? "");
  const gap = WIDTH - l.length - r.length;
  if (gap < 1) return l + "\n" + r.padStart(WIDTH);
  return l + " ".repeat(gap) + r;
}

function optionsText(item) {
  return (item.options || []).map((op) => op.name).filter(Boolean).join(", ");
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

function printHTML(css, body) {
  const w = window.open("", "_blank");
  if (!w) return;
  // body is already HTML-escaped; only the controlled \u0001 markers become <strong>.
  const rendered = escapeHtml(body).replace(/\u0001(.*?)\u0001/g, "<strong>$1</strong>");
  const doc = w.document;
  const style = doc.createElement("style");
  style.textContent = css;
  doc.head.appendChild(style);
  const pre = doc.createElement("pre");
  pre.innerHTML = rendered + "\n";
  doc.body.appendChild(pre);
  w.focus();
  w.print();
}

export function printCustomerReceipt(order, settings) {
  const o = order || {};
  const s = settings || {};
  const L = [];
  L.push(center(s.name || "METROS"));
  if (s.address) L.push(center(s.address));
  if (s.phone) L.push(center("Tel: " + s.phone));
  L.push(LINE);
  L.push(`Order: ${o.order_number}`);
  L.push("Placed: " + new Date(o.created_at).toLocaleString("en-GB"));
  L.push((o.type || "").toUpperCase());
  if (o.collection_time && o.collection_time !== "asap") L.push("\u0001" + row("SCHEDULED", (o.type === "delivery" ? "Deliver " : "Collect ") + o.collection_time) + "\u0001");
  else L.push("ASAP");
  if (o.customer && (o.customer.name || o.customer.phone)) {
    L.push(LINE);
    L.push("CUSTOMER");
    if (o.customer.name) L.push(o.customer.name);
    if (o.customer.phone) L.push("Tel: " + o.customer.phone);
    if (o.user_id) {
      const _tot = o.customer_total_orders != null ? o.customer_total_orders : ((o.customer_past_orders || 0) + 1);
      L.push("\u0001" + row("PAST ORDERS", String(_tot)) + "\u0001");
    }
  }
  L.push(LINE);
  (o.items || []).forEach((i) => {
    L.push(row(`${i.quantity}x ${i.name}`, money(i.line_total)));
    (i.options || []).forEach((op) => { if (op && op.name) L.push(row("   " + op.name, money(op.price || 0))); });
    if (i.special_instructions) L.push("   " + i.special_instructions);
  });
  L.push(LINE);
  L.push(row("Subtotal", money(o.subtotal)));
  if (o.delivery_fee) L.push(row("Delivery", money(o.delivery_fee)));
  const disc = (o.discount || 0) + (o.points_discount || 0);
  if (disc) L.push(row("Discount", "-" + money(disc)));
  if (o.reward_discount) L.push(row("Loyalty reward", "-" + money(o.reward_discount)));
  L.push(row("TOTAL", money(o.total)));
  if (o.user_id) {
    L.push(LINE);
    L.push("Loyalty Points");
    if (o.customer_loyalty_points != null) L.push(row("Balance", String(o.customer_loyalty_points)));
    L.push(row("Earned this order", "+" + (o.points_earned || 0)));
    L.push(row("Spent this order", "-" + (o.points_redeemed || 0)));
    if (o.reward_cycle_size) {
      const past = o.customer_past_orders || 0;
      if (o.reward_order) L.push(center("*** REWARD ORDER ***"));
      else L.push(row("Order in cycle", `${(past % o.reward_cycle_size) + 1}/${o.reward_cycle_size}`));
    }
  }
  L.push(LINE);
  L.push(center("Thank you for your order!"));
  printHTML(CSS, L.join("\n"));
}

export function printKitchenTicket(order) {
  const o = order || {};
  const L = [];
  L.push(center("*** KITCHEN ***"));
  L.push(`Order: ${o.order_number}`);
  L.push("Placed: " + new Date(o.created_at).toLocaleString("en-GB"));
  L.push((o.type || "").toUpperCase());
  if (o.collection_time && o.collection_time !== "asap") L.push("*** SCHEDULED " + (o.type === "delivery" ? "DELIVERY" : "COLLECTION") + " " + o.collection_time + " ***");
  else L.push("ASAP");
  if (o.user_id && o.reward_cycle_size != null) {
    const past = o.customer_past_orders || 0;
    L.push(`Customer orders: ${past}`);
    L.push(`Cycle: ${(past % o.reward_cycle_size) + 1}/${o.reward_cycle_size}${o.reward_order ? "  *REWARD*" : ""}`);
  }
  L.push(LINE);
  (o.items || []).forEach((i) => {
    L.push(`${i.quantity}x ${i.name}`);
    const opt = optionsText(i);
    if (opt) L.push("   (" + opt + ")");
    if (i.special_instructions) L.push("   ! " + i.special_instructions);
  });
  L.push(LINE);
  if (o.note) L.push("NOTE: " + o.note);
  printHTML(KITCHEN_CSS, L.join("\n"));
}

export function printWalkInReceipt(data, settings) {
  const s = settings || {};
  const d = data || {};
  const L = [];
  L.push(center(s.name || "METROS"));
  if (s.address) L.push(center(s.address));
  if (s.phone) L.push(center("Tel: " + s.phone));
  L.push(LINE);
  L.push(center("WALK-IN LOYALTY"));
  L.push("Date: " + new Date().toLocaleString("en-GB"));
  if (d.customerName) L.push("Customer: " + d.customerName);
  L.push(LINE);
  if (d.type === "redeem") {
    L.push(row("Points redeemed", "-" + (d.points || 0)));
    L.push("\u0001" + row("Value off order", money(d.value || 0)) + "\u0001");
  } else {
    if (d.amount != null) L.push(row("Order amount", money(d.amount)));
    L.push("\u0001" + row("Points earned", "+" + (d.points || 0)) + "\u0001");
  }
  L.push(LINE);
  L.push(row("New balance", (d.balance || 0) + " pts"));
  L.push(LINE);
  L.push(center("Thank you for your order!"));
  printHTML(CSS, L.join("\n"));
}
